import { Dialog, DialogContent, Stack, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createApplicant, resetCreateApplicant } from 'store/slices/employer/applicants/applicant-actions-slice';
import { RootState } from 'store';
import { ShButton, ShFileUploadButton, ShTextFieldV2 } from 'shared/SharedStyles/ShInputs';
import { UploadResumeIcon } from 'assets/Icons/UploadResume';
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { Snackbar } from '@mui/material';
import { AppDispatch } from 'shared/SharedModels';
import { ShPaper } from 'shared/SharedStyles/ShSurfaces';

interface AddApplicantDialogProps {
    open: boolean;
    onClose: () => void;
    employerJobId: number;
}

export const AddApplicantDialog = ({ open, onClose, employerJobId }: AddApplicantDialogProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { createApplicantStatus, createApplicantResponse } = useSelector((state: RootState) => state.employer.applicants.applicantActions);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        resume: null as File | null
    });

    const handleSubmit = async () => {
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber || !formData.resume) {
            return;
        }

        try {
            await dispatch(createApplicant({
                ...formData,
                employerJobId,
                resume: formData.resume as File 
            })).unwrap();
            onClose();
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                resume: null
            });
        } catch (error) {
            console.error('Error creating applicant:', error);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setFormData(prev => ({
                ...prev,
                resume: event.target.files![0]
            }));
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <ShPaper variant="outlined" headerSection>
                    <Typography variant="h6">Create New Applicant</Typography>
                    <Typography variant="body2" color="text.secondary">Track new applicants for this job added manually or sourced externally.</Typography>
                </ShPaper>
                <DialogContent>
                    <Stack spacing={2}>
                        <ShTextFieldV2 label="First Name" value={formData.firstName} onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                        <ShTextFieldV2 label="Last Name" value={formData.lastName} onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                        <ShTextFieldV2 label="Email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                        <ShTextFieldV2 label="Phone Number" value={formData.phoneNumber} onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                        <ShFileUploadButton component="label" startIcon={<UploadResumeIcon />} disabled={createApplicantStatus === 'pending'}>
                            {formData.resume ? formData.resume.name : 'Upload Resume'}
                            <input type="file" hidden accept=".pdf,.doc,.docx,.txt,.rtf" onChange={handleFileChange} disabled={createApplicantStatus === 'pending'} />
                        </ShFileUploadButton>
                    </Stack>
                </DialogContent>
                <Stack direction="row" spacing={2} justifyContent="space-between" p={1}>
                    <ShButton onClick={onClose} disabled={createApplicantStatus === 'pending'}>Cancel</ShButton>
                    <ShButton onClick={handleSubmit} variant="contained" disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber || !formData.resume || createApplicantStatus === 'pending'} startIcon={createApplicantStatus === 'pending' ? <CircularProgress size={20} color="inherit" /> : null}>
                        {createApplicantStatus === 'pending' ? 'Creating...' : 'Create Applicant'}
                    </ShButton>
                </Stack>
            </Dialog>
            <Snackbar open={createApplicantStatus === 'success' || createApplicantStatus === 'failed'} autoHideDuration={6000} onClose={() => dispatch(resetCreateApplicant())}>
                <ShAlert severity={createApplicantStatus === 'success' ? 'success' : 'error'} onClose={() => dispatch(resetCreateApplicant())}>
                    {createApplicantResponse}
                </ShAlert>
            </Snackbar>
        </>
    );
}; 